import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Popover,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";
import { OcOrderListItem } from "../../types/oc";

// El selector solo necesita estos cuatro campos, así que sirve igual para el listado
// completo (OcOrderListItem, filtro del histórico) que para las opciones ya filtradas por
// cliente (OcOrderOption, formulario de factura).
export type OcOrderSelectorItem = Pick<
  OcOrderListItem,
  "id" | "ocNumber" | "campaign" | "clientLabel"
>;

interface OcOrderSelectorProps {
  orders: OcOrderSelectorItem[];
  selectedId: string | null;
  onPick: (order: OcOrderSelectorItem) => void;
  // When provided, renders a "Todas" clear entry — used for the history filter.
  onClear?: () => void;
  disabled?: boolean;
}

// Display label for an OC: "{ocNumber} — {campaign} ({clientLabel})".
export const ocOrderLabel = (o: OcOrderSelectorItem): string => {
  const campaign = o.campaign ? ` — ${o.campaign}` : "";
  const client = o.clientLabel ? ` (${o.clientLabel})` : "";
  return `${o.ocNumber}${campaign}${client}`;
};

// Searchable Orden de Compra dropdown — same Popover pattern as the client
// selector. Reused both for the (required) create-form field and the history
// filter (with a "Todas" clear entry).
const OcOrderSelector: React.FC<OcOrderSelectorProps> = ({
  orders,
  selectedId,
  onPick,
  onClear,
  disabled,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState("");

  const open = Boolean(anchorEl);

  const selected = useMemo(
    () => orders.find((o) => o.id === selectedId) || null,
    [orders, selectedId]
  );

  const filteredOrders = useMemo(() => {
    if (!search.trim()) return orders;
    const q = search.toLowerCase();
    return orders.filter(
      (o) =>
        o.ocNumber.toLowerCase().includes(q) ||
        (o.campaign || "").toLowerCase().includes(q) ||
        (o.clientLabel || "").toLowerCase().includes(q)
    );
  }, [orders, search]);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setSearch("");
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const displayText = selected
    ? ocOrderLabel(selected)
    : t("invoices.ocSelector.select");

  return (
    <Box>
      <Button
        variant="outlined"
        fullWidth
        disabled={disabled}
        onClick={handleOpen}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          justifyContent: "space-between",
          textTransform: "none",
          color: selected ? "inherit" : theme.palette.neutral["600"],
          borderColor: theme.palette.neutral["300"],
        }}
      >
        <Typography
          variant="pSmall"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "left",
          }}
        >
          {displayText}
        </Typography>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: { sx: { width: anchorEl?.clientWidth, mt: 0.5 } },
        }}
      >
        <Box
          sx={{
            p: 1.5,
            borderBottom: `1px solid ${theme.palette.neutral["200"]}`,
          }}
        >
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={t("invoices.ocSelector.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>

        {onClear && (
          <>
            <ListItemButton
              onClick={() => {
                onClear();
                handleClose();
              }}
            >
              <ListItemText primary={t("invoices.history.allFem")} />
            </ListItemButton>
            <Divider />
          </>
        )}

        <List sx={{ maxHeight: "34rem", overflowY: "auto", py: 0 }}>
          {filteredOrders.map((o) => (
            <ListItemButton
              key={o.id}
              selected={o.id === selectedId}
              onClick={() => {
                onPick(o);
                handleClose();
              }}
            >
              <ListItemText primary={ocOrderLabel(o)} />
            </ListItemButton>
          ))}
          {filteredOrders.length === 0 && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="pSmall" color={theme.palette.neutral["600"]}>
                {t("invoices.ocSelector.noResults")}
              </Typography>
            </Box>
          )}
        </List>
      </Popover>
    </Box>
  );
};

export default OcOrderSelector;
